import React, { useState, useRef, useEffect } from "react";
import PageLayout from "@/components/PageLayout";
import FaqSection from "@/components/FaqSection";
import { Link, useNavigate } from "react-router-dom";
import { base44 } from "@/api/base44Client";
import { MapPin, Phone, Clock } from "lucide-react";
import { BUSINESS, ADDRESS_LINES, HOURS_LINES } from "@/lib/business";

export default function Contact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "", consent_given: false });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);

  // WCAG §3.3.1 Error Identification — move focus to the message so it is announced
  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.consent_given) {
      setError("Please agree to the privacy policy to submit.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await base44.entities.ContactSubmission.create(form);
      base44.analytics.track({ eventName: "generate_lead", properties: { source: "contact_form" } });
      navigate("/thank-you");
    } catch (err) {
      setError(`Something went wrong sending your message. Please try again or call us at ${BUSINESS.phoneDisplay}.`);
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">Visit Our Neapolitan Restaurant on the Upper East Side</h1>
      <p className="font-body text-muted-foreground mb-12 max-w-2xl">
        Find us at {BUSINESS.streetAddress}, {BUSINESS.crossStreets.toLowerCase()}, among the Upper East Side Italian restaurants of Lenox Hill, and one of the few Upper East Side Neapolitan restaurants with a brick oven, serving authentic Neapolitan pizza in the Upper East Side. We'd love to hear from you: questions, private events, or catering inquiries.
      </p>

      <div className="grid md:grid-cols-2 gap-16">
        <div>
          <h2 id="contact-form-heading" className="font-display text-2xl text-foreground mb-6">Send a Message</h2>
          <form onSubmit={handleSubmit} className="space-y-5" aria-labelledby="contact-form-heading" noValidate={false}>
            <p className="font-body text-xs text-muted-foreground">
              Fields marked with an asterisk (<span className="text-primary">*</span>) are required.
            </p>
            <div>
              <label htmlFor="name" className="font-body text-sm text-foreground block mb-1">
                Full Name <span className="text-primary" aria-hidden="true">*</span><span className="sr-only">(required)</span>
              </label>
              <input id="name" name="name" type="text" required aria-required="true" autoComplete="name" value={form.name} onChange={handleChange} className="w-full px-4 py-3 border border-border bg-background font-body text-sm focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring" />
            </div>
            <div>
              <label htmlFor="email" className="font-body text-sm text-foreground block mb-1">
                Email Address <span className="text-primary" aria-hidden="true">*</span><span className="sr-only">(required)</span>
              </label>
              <input id="email" name="email" type="email" required aria-required="true" autoComplete="email" inputMode="email" value={form.email} onChange={handleChange} className="w-full px-4 py-3 border border-border bg-background font-body text-sm focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring" />
            </div>
            <div>
              <label htmlFor="phone" className="font-body text-sm text-foreground block mb-1">
                Phone Number <span className="text-primary" aria-hidden="true">*</span><span className="sr-only">(required)</span>
              </label>
              <input id="phone" name="phone" type="tel" required aria-required="true" autoComplete="tel" inputMode="tel" value={form.phone} onChange={handleChange} className="w-full px-4 py-3 border border-border bg-background font-body text-sm focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring" />
            </div>
            <div>
              <label htmlFor="message" className="font-body text-sm text-foreground block mb-1">
                Message <span className="text-primary" aria-hidden="true">*</span><span className="sr-only">(required)</span>
              </label>
              <textarea id="message" name="message" required aria-required="true" rows={5} value={form.message} onChange={handleChange} className="w-full px-4 py-3 border border-border bg-background font-body text-sm focus-visible:outline-2 focus-visible:outline-offset-0 focus-visible:outline-ring resize-y" />
            </div>
            <div className="flex items-start gap-2">
              <input id="consent" name="consent_given" type="checkbox" aria-required="true" checked={form.consent_given} onChange={handleChange} className="mt-0.5 w-6 h-6 shrink-0 focus-visible:outline-2 focus-visible:outline-ring" />
              <label htmlFor="consent" className="font-body text-xs text-muted-foreground">
                I agree to the <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link> and consent to having my data collected and stored to respond to my inquiry.
              </label>
            </div>
            <div aria-live="assertive" role="alert" tabIndex={-1} ref={errorRef} className="focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring rounded-sm">
              {error && <p className="font-body text-sm text-destructive">{error}</p>}
            </div>
            <button type="submit" disabled={submitting} aria-label="Send your message and inquiry to Donna Margherita" className="w-full min-h-[44px] py-3 bg-primary text-primary-foreground font-body text-xs tracking-widest uppercase hover:bg-primary/90 active:scale-95 transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:opacity-50">
              {submitting ? 'Sending...' : 'Send Message'}
            </button>
          </form>
        </div>

        <div className="space-y-8">
          <h2 className="font-display text-2xl text-foreground mb-6">Address and Directions</h2>
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-body text-sm font-medium text-foreground">{BUSINESS.name}</p>
                {ADDRESS_LINES.map((line) => (
                  <p key={line} className="font-body text-sm text-muted-foreground">{line}</p>
                ))}
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-body text-sm font-medium text-foreground">Reservations</p>
                <a
                  href={BUSINESS.phoneHref}
                  aria-label={`Call ${BUSINESS.name} to reserve a table at ${BUSINESS.phoneDisplay}`}
                  className="inline-flex items-center min-h-[44px] font-body text-sm text-primary hover:underline"
                >
                  {BUSINESS.phoneDisplay}
                </a>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Clock className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-body text-sm font-medium text-foreground">Hours</p>
                {HOURS_LINES.map((line) => (
                  <p key={line} className="font-body text-sm text-muted-foreground">{line}</p>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <a
              href={BUSINESS.reservationUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Reserve a table at Donna Margherita on OpenTable (opens in a new tab)"
              className="inline-flex items-center justify-center w-full min-h-[44px] text-center py-3 border border-primary text-primary font-body text-xs tracking-widest uppercase hover:bg-primary hover:text-primary-foreground transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              Reserve on OpenTable<span className="sr-only"> (opens in a new tab)</span>
            </a>
          </div>
        </div>
      </div>
      <FaqSection path="/contact" compact />
    </PageLayout>
  );
}